import { FormControl, IconButton, InputLabel, makeStyles, MenuItem, Select, TextField } from '@material-ui/core';
import * as React from 'react';
import {
  GroupByAggregateFunction,
  GroupByFilter,
  GroupByOperator,
  GroupByValues,
  TransactionalFilter,
  GroupByPredicates,
  SegmentAttributeOperator
} from '../../../models/segments';
import { CollapsibleSection } from '../../common/collapsible-section';
import { colors, blue } from '../../../theme/colors';
import clsx from 'clsx';
import * as styles from './attribute-row.module.scss';
import { MultiselectChip } from './multiselect-chip';
import { Clear, Delete } from '@material-ui/icons';
import * as buildStyles from './build-segment.module.scss';
import AddAttribute from './add-attribute-button';
import _ from 'lodash';
import OperatorSelect from './operator-select';

const breakpoints = {
  xs: 640,
  sm: 1020,
  md: 1200
};

const useStyles = makeStyles((theme) => ({
  groupByContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '15px 0 10px'
  },
  headerMargin: {
    marginTop: 0
  },
  inputs: {
    margin: '4px 12px',
    '& .MuiInputLabel-asterisk': {
      color: colors.rustRed
    },
    width: '300px'
  },
  attributeSelect: {
    [theme.breakpoints.down(breakpoints.sm)]: {
      flex: 1
    },
    [theme.breakpoints.up(breakpoints.sm)]: {
      flex: '0 0 200px'
    },
    [theme.breakpoints.up(breakpoints.md)]: {
      flex: '0 0 300px'
    }
  },
  attribute: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    paddingTop: 10,
    paddingBottom: 10
  },
  filterOperatorSelect: {
    flex: '0 0 200px',
    [theme.breakpoints.up(breakpoints.xs)]: {
      flex: '0 0 225px'
    }
  },
  addControls: {
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'flex-end'
  },
  valueInput: {
    width: '230px'
  },
  delete: {
    flex: '0 0 48px',
    marginRight: 12,
    zIndex: 1,
    '&.Mui-disabled': {
      opacity: 0.5
    }
  }
}));

interface AttributeGroupByProps {
  transactionalFilterOptions: TransactionalFilter[];
  selectedGroupBy: GroupByFilter | null;
  updateGroupBy: (updatedGroupBy: GroupByFilter) => void;
}

const defaultGroupByValuesPredicates: GroupByPredicates = {
  aggregateFunction: GroupByAggregateFunction.Sum,
  aggregateOperator: GroupByOperator['Is Greater Than'],
  value: '',
  valueFilterColumnName: '',
  valueFilterId: undefined,
  valueFilterName: ''
};

const defaultGroupByValues: GroupByValues = {
  groupByOperator: 0,
  groupByPredicates: [{ ...defaultGroupByValuesPredicates }]
};

const AttributeGroupBy = ({ transactionalFilterOptions, selectedGroupBy, updateGroupBy }: AttributeGroupByProps) => {
  const classes = useStyles();

  const getTransactionalFilter = (filterId: number) => {
    return transactionalFilterOptions.find((option) => option.filterId === filterId);
  };

  const [selectionCollapsed, setSelectionCollapsed] = React.useState<boolean>(true);
  const [selectedGroupByOption, setSelectedGroupByOption] = React.useState<TransactionalFilter>(
    selectedGroupBy?.groupByFilterId ? getTransactionalFilter(selectedGroupBy.groupByFilterId) : null
  );

  const [groupByValues, setGroupByValues] = React.useState<GroupByValues>(
    selectedGroupBy?.groupByValues ?? defaultGroupByValues
  );

  React.useEffect(() => {
    if (!selectedGroupByOption?.filterId) {
      updateGroupBy(null);
    } else {
      const updatedGroupByValues = _.cloneDeep(groupByValues);
      const groupBy: GroupByFilter = {
        groupByFilterId: selectedGroupByOption.filterId,
        groupByFilterColumnName: selectedGroupByOption.columnName,
        groupByFilterName: selectedGroupByOption.name,
        groupByValues: updatedGroupByValues
      };
      updateGroupBy(groupBy);
    }
  }, [selectedGroupByOption, groupByValues]);

  const clearGroupBy = () => {
    setSelectedGroupByOption(null);
  };

  const updateGroupByValues = (updatedValue: GroupByPredicates, groupByRow: number) => {
    const clonedFilter = _.cloneDeep(groupByValues);
    clonedFilter.groupByPredicates[groupByRow] = { ...updatedValue };
    setGroupByValues(clonedFilter);
  };

  const updateGroupByOperator = (operator: SegmentAttributeOperator) => {
    const clonedFilter = _.cloneDeep(groupByValues);
    clonedFilter.groupByOperator = operator;
    setGroupByValues(clonedFilter);
  };

  const renderFilterValues = (value: string) => {
    if (!value || !selectedGroupByOption) {
      return '';
    }

    return (
      <div className={buildStyles.filterChips}>
        <MultiselectChip
          label={selectedGroupByOption.columnName}
          size="small"
          deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="remove group by" />}
          onDelete={clearGroupBy}
        />
      </div>
    );
  };

  const addAnotherGroupByFilter = () => {
    const clonedFilter = _.cloneDeep(groupByValues);
    clonedFilter.groupByPredicates.push(defaultGroupByValuesPredicates);
    setGroupByValues(clonedFilter);
  };

  const handleDeleteGroupByValueRow = (rowIndex: number) => {
    const clonedFilters = _.cloneDeep(groupByValues);
    clonedFilters.groupByPredicates.splice(rowIndex, 1);
    if (clonedFilters.groupByPredicates.length === 0) {
      clonedFilters.groupByPredicates.push(defaultGroupByValuesPredicates);
    }
    setGroupByValues(clonedFilters);
  };

  return (
    <CollapsibleSection
      title="Group By (Optional)"
      collapsed={selectionCollapsed}
      onCollapse={setSelectionCollapsed}
      className={classes.headerMargin}
    >
      <div className={classes.groupByContainer}>
        <FormControl size="small" variant="outlined" className={classes.inputs}>
          <InputLabel id="group-by-selection">What do you want to group by?</InputLabel>
          <Select
            labelId="group-by-selection"
            value={selectedGroupByOption?.filterId ?? ''}
            renderValue={renderFilterValues}
            onChange={(event: any) => {
              const selectedOption = transactionalFilterOptions.find(
                (option) => option.filterId === Number(event.target.value)
              );
              setSelectedGroupByOption(selectedOption);
            }}
          >
            {transactionalFilterOptions?.map((filter) => (
              <MenuItem value={filter.filterId} key={`Group-By-Option-${filter.filterId}`}>
                {filter.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {groupByValues.groupByPredicates.map((groupByPredicate, index) => (
          <GroupByPredicatesRow
            key={`${groupByPredicate.valueFilterId} - ${index}`}
            groupByPredicate={groupByPredicate}
            groupByOperator={groupByValues.groupByOperator}
            filterSelectDisabled={!selectedGroupByOption}
            transactionalFilterOptions={transactionalFilterOptions.filter((option) => option.filterType === 'range')}
            predicateIndex={index}
            updateGroupByValueRow={updateGroupByValues}
            updateGroupByOperator={updateGroupByOperator}
            handleDeleteGroupByValueRow={handleDeleteGroupByValueRow}
          />
        ))}
        <div className={classes.addControls}>
          <AddAttribute
            label="Add another value"
            handleClick={addAnotherGroupByFilter}
            disabled={!selectedGroupByOption}
          />
        </div>
      </div>
    </CollapsibleSection>
  );
};

interface GroupByPredicatesRowProps {
  groupByPredicate: GroupByPredicates;
  groupByOperator: SegmentAttributeOperator;
  transactionalFilterOptions: TransactionalFilter[];
  filterSelectDisabled: boolean;
  predicateIndex: number;
  updateGroupByValueRow: (updatedValue: GroupByPredicates, groupByRow: number) => void;
  updateGroupByOperator: (operator: SegmentAttributeOperator) => void;
  handleDeleteGroupByValueRow: (rowIndex: number) => void;
}

const GroupByPredicatesRow = ({
  groupByPredicate,
  groupByOperator,
  transactionalFilterOptions,
  filterSelectDisabled,
  predicateIndex,
  updateGroupByValueRow,
  updateGroupByOperator,
  handleDeleteGroupByValueRow
}: GroupByPredicatesRowProps) => {
  const classes = useStyles();

  const updatePredicateValue = (updatedValue: GroupByPredicates) => {
    updateGroupByValueRow(updatedValue, predicateIndex);
  };

  return (
    <div className={classes.attribute}>
      {predicateIndex === 0 && <div className={styles.attributeOperatorText}></div>}
      {predicateIndex === 1 && (
        <OperatorSelect operator={groupByOperator} handleChange={(operator) => updateGroupByOperator(operator)} />
      )}
      {predicateIndex > 1 && (
        <div className={styles.attributeOperatorText}>
          <p className={styles.groupOperatorText}>{SegmentAttributeOperator[groupByOperator]}</p>
        </div>
      )}
      <FormControl size="small" variant="outlined" className={classes.inputs} required={true}>
        <InputLabel id="target-by-selection">What do you want to target?</InputLabel>
        <Select
          labelId="target-by-selection"
          value={groupByPredicate?.valueFilterId ?? ''}
          disabled={filterSelectDisabled}
          onChange={(event: any) => {
            const selectedOption = transactionalFilterOptions.find(
              (option) => option.filterId === Number(event.target.value)
            );
            updatePredicateValue({
              ...groupByPredicate,
              valueFilterId: selectedOption.filterId,
              valueFilterColumnName: selectedOption.columnName,
              valueFilterName: selectedOption.name
            });
          }}
        >
          {transactionalFilterOptions?.map((filter) => {
            return (
              <MenuItem value={filter.filterId} key={`Target-Option-${filter.filterId}`}>
                {filter.name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <FormControl
        size="small"
        variant="outlined"
        className={clsx(classes.inputs, classes.filterOperatorSelect)}
        required={true}
        disabled={filterSelectDisabled}
      >
        <InputLabel id="aggregate-function-filter-selection">Aggregate Function</InputLabel>
        <Select
          labelId="aggregate-function-filter-selection"
          value={groupByPredicate.aggregateFunction}
          disabled={filterSelectDisabled}
          onChange={(event: any) => {
            updatePredicateValue({
              ...groupByPredicate,
              aggregateFunction: event.target.value as GroupByAggregateFunction
            });
          }}
        >
          <MenuItem key="Sum" value={GroupByAggregateFunction['Sum']}>
            Sum
          </MenuItem>
          <MenuItem key="Average" value={GroupByAggregateFunction['Average']}>
            Average
          </MenuItem>
          <MenuItem key="Count" value={GroupByAggregateFunction['Count']}>
            Count
          </MenuItem>
          <MenuItem key="Count Distinct" value={GroupByAggregateFunction['Count Distinct']}>
            Count Distinct
          </MenuItem>
          <MenuItem key="Minimum" value={GroupByAggregateFunction['Minimum']}>
            Minimum
          </MenuItem>
          <MenuItem key="Maximum" value={GroupByAggregateFunction['Maximum']}>
            Maximum
          </MenuItem>
        </Select>
      </FormControl>
      <FormControl
        size="small"
        variant="outlined"
        className={clsx(classes.inputs, classes.filterOperatorSelect)}
        required={true}
        disabled={filterSelectDisabled}
      >
        <InputLabel id="operator-filter-selection">Operator</InputLabel>
        <Select
          labelId="operator-filter-selection"
          value={groupByPredicate.aggregateOperator}
          disabled={filterSelectDisabled}
          onChange={(event: any) => {
            updatePredicateValue({
              ...groupByPredicate,
              aggregateOperator: event.target.value as GroupByOperator
            });
          }}
        >
          <MenuItem key="Is Greater Than" value={GroupByOperator['Is Greater Than']}>
            Is Greater Than
          </MenuItem>
          <MenuItem key="Is Less Than" value={GroupByOperator['Is Less Than']}>
            Is Less Than
          </MenuItem>
          <MenuItem key="Is Equal To" value={GroupByOperator['Is Equal To']}>
            Is Equal To
          </MenuItem>
        </Select>
      </FormControl>
      <FormControl
        size="small"
        variant="outlined"
        className={clsx(classes.inputs, classes.filterOperatorSelect)}
        required={true}
        disabled={filterSelectDisabled}
      >
        <TextField
          required={true}
          size="small"
          variant="outlined"
          label="Value"
          value={groupByPredicate.value}
          className={classes.valueInput}
          onChange={(e) =>
            updatePredicateValue({
              ...groupByPredicate,
              value: e.target.value
            })
          }
          disabled={filterSelectDisabled}
          inputProps={{
            type: 'number'
          }}
        />
      </FormControl>
      <IconButton
        onClick={() => handleDeleteGroupByValueRow(predicateIndex)}
        className={classes.delete}
        disabled={predicateIndex === 0 && !groupByPredicate?.valueFilterId}
      >
        <Delete htmlColor={blue[600]} />
      </IconButton>
    </div>
  );
};

export default AttributeGroupBy;
